import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresTerms, setRequiresTerms] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const navigate = useNavigate();

  // Verificar token existente al cargar la aplicación
  useEffect(() => {
    const token = localStorage.getItem('auth-token');
    if (token) {
      verifyToken(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setUser(response.data.user);

      // Redirigir según el rol
      const currentPath = window.location.pathname;
      if (currentPath === '/login' || currentPath === '/') {
        redirectUserByRole(response.data.user.role);
      }
    } catch (error) {
      // Token inválido o expirado
      localStorage.removeItem('auth-token');
      if (window.location.pathname !== '/login') {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/login', { email, password });

      if (response.data.requiresTerms) {
        // Cliente necesita aceptar términos
        console.log('🔔 Términos requeridos - mostrando modal');
        setRequiresTerms(true);
        setPendingUserId(response.data.userId);
        return {
          success: false,
          requiresTerms: true,
          message: 'Debe aceptar los términos y condiciones para continuar'
        };
      }

      const { token, user } = response.data;

      // Guardar token
      localStorage.setItem('auth-token', token);

      // Establecer usuario
      setUser(user);

      // Mostrar mensaje de bienvenida
      toast.success(`¡Bienvenido ${user.fullName}!`);

      // Redirigir según el rol
      redirectUserByRole(user.role);

      return { success: true, user };
    } catch (error) {
      console.log('❌ Error en login:', error.response?.data);

      // Manejar específicamente el caso de términos no aceptados
      if (error.response?.status === 403 && error.response?.data?.requiresTerms) {
        console.log('🔔 Términos requeridos - mostrando modal desde error 403');
        setRequiresTerms(true);
        setPendingUserId(error.response.data.userId);
        return {
          success: false,
          requiresTerms: true,
          message: 'Debe aceptar los términos y condiciones para continuar'
        };
      }

      const message = error.response?.data?.error || 'Error al iniciar sesión';
      toast.error(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const acceptTerms = async () => {
    try {
      setIsLoading(true);
      await api.post('/auth/accept-terms', { userId: pendingUserId });

      // Intentar login automático (el usuario ya proporcionó credenciales)
      toast.success('Términos aceptados correctamente');
      setRequiresTerms(false);
      setPendingUserId(null);

      // Aquí podrías implementar un re-login automático
      // Por ahora, redirigir al login
      navigate('/login');

      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Error al aceptar términos';
      toast.error(message);
      return { success: false, message };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('auth-token');
    setUser(null);
    setRequiresTerms(false);
    setPendingUserId(null);
    navigate('/login');
    toast.success('Sesión cerrada correctamente');
  };

  const redirectUserByRole = (role) => {
    switch (role) {
      case 'admin':
        navigate('/admin');
        break;
      case 'client':
        navigate('/client');
        break;
      default:
        navigate('/login');
    }
  };

  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  const value = {
    user,
    isLoading,
    requiresTerms,
    pendingUserId,
    login,
    logout,
    acceptTerms,
    updateUser,
    hasRole,
    hasAnyRole,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isClient: user?.role === 'client'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};